// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Skyhold — the solo opponent. In a deep abstract the bot IS the product for one player, so this is
// a real engine: negamax with alpha-beta, iterative deepening, winning-move-first ordering and a
// positional evaluation, over the same rules.ts every human move goes through.
//
// Three things here are deliberate and load-bearing:
//
//   * NO requestAnimationFrame ANYWHERE. The search is budgeted with Date.now() and started from a
//     setTimeout. A backgrounded tab throttles rAF to nothing, and a bot that cannot finish its
//     turn is a hung game, not a slow one.
//   * THE SIM DRIVES THE SAME SEARCH, budgeted by DEPTH (and a node cap) rather than a wall clock,
//     so tests/balance.test.ts is reproducible instead of machine-dependent.
//   * The node pool copies into preallocated states rather than allocating one per node — the
//     entire search touches five typed arrays and no garbage.

import { makeRng, type Rng } from '@ben-gy/game-engine/rng';
import {
  DOME,
  NONE,
  PH_PLACE,
  PH_PLAY,
  WIN_LEVEL,
  applyPly,
  buildersOf,
  cloneState,
  genPlies,
  isOver,
  neighbours,
  packPlace,
  plyBuild,
  type State,
} from './rules';

export interface Tier {
  id: string;
  name: string;
  blurb: string;
  depth: number;
  timeMs: number;
  /** Novice: the threat terms are zeroed, so it defends and climbs but cannot construct a fork. */
  flat: boolean;
}

export const TIERS: Tier[] = [
  { id: 'novice', name: 'Novice', blurb: 'Climbs, blocks, never blunders a win — but never sets a trap.', depth: 1, timeMs: 60, flat: true },
  { id: 'adept', name: 'Adept', blurb: 'Looks a few turns ahead and will punish a loose builder.', depth: 3, timeMs: 400, flat: false },
  { id: 'skywright', name: 'Skywright', blurb: 'Searches deep, builds forks, and closes the trap early.', depth: 5, timeMs: 1200, flat: false },
];

export const DEFAULT_TIER = 'novice';

export function tierOf(id: string | null | undefined): Tier {
  if (typeof id === 'string') {
    const t = TIERS.find((x) => x.id === id);
    if (t) return t;
  }
  return TIERS[0];
}

const MATE = 1_000_000;

export interface SearchOpts {
  depth: number;
  /** Wall-clock cap. 0 disables it — which is what the sim uses. */
  timeMs?: number;
  /** Hard node cap, so a sim run is bounded regardless of machine speed. */
  nodes?: number;
  flat?: boolean;
  /** Small random tie-break so repeat games are not byte-identical. */
  noise?: number;
  rng?: Rng;
}

export interface SearchResult {
  ply: number;
  score: number;
  depth: number;
  nodes: number;
  ms: number;
}

// ── evaluation ──────────────────────────────────────────────────────────────────
const H = [0, 22, 66, 0, 0]; // standing height; level 3 is terminal so it never scores
const W_CLIMB = 13;
const W_MOBILITY = 2;
const W_CENTRE = 3;
const W_THREAT = 46;
const W_DOUBLE = 70;

/**
 * Score the position for `me`, in centi-blocks. Terms, in order of how much they matter:
 * standing height, being able to climb next turn, a live threat (on a 2, beside a raisable 2),
 * a DOUBLE threat (the fork — two answers needed, one turn to give), then mobility and centre.
 */
export function evaluate(s: State, me: number, flat = false): number {
  const nb = neighbours(s.w, s.h);
  const cx = (s.w - 1) / 2;
  const cy = (s.h - 1) / 2;
  let score = 0;

  for (let seat = 0; seat < 2; seat++) {
    const sign = seat === me ? 1 : -1;
    let threats = 0;
    for (const b of buildersOf(s, seat)) {
      const at = s.pos[b];
      if (at < 0) continue;
      const lv = s.lev[at];
      let sub = H[lv];

      let climbs = 0;
      let mob = 0;
      for (const n of nb[at]) {
        if (s.lev[n] >= DOME || s.occ[n] !== -1) continue;
        if (s.lev[n] <= lv + 1) mob++;
        if (s.lev[n] === lv + 1) climbs++;
      }
      sub += Math.min(climbs, 2) * W_CLIMB + mob * W_MOBILITY;

      const x = at % s.w;
      const y = (at / s.w) | 0;
      sub += (2 - (Math.abs(x - cx) + Math.abs(y - cy)) / 2) * W_CENTRE;

      if (!flat && lv === WIN_LEVEL - 1) {
        // On a 2 beside an empty 2: raise it and the climb is live next turn.
        for (const n of nb[at]) {
          if (s.occ[n] !== -1 || s.lev[n] !== WIN_LEVEL - 1) continue;
          threats++;
        }
      }
      score += sign * sub;
    }
    if (!flat && threats > 0) {
      score += sign * (W_THREAT + (threats > 1 ? W_DOUBLE : 0));
    }
  }
  return score;
}

// ── the search ──────────────────────────────────────────────────────────────────
interface Ctx {
  pool: State[];
  nodes: number;
  deadline: number;
  maxNodes: number;
  aborted: boolean;
  flat: boolean;
}

function copyInto(dst: State, src: State): void {
  dst.lev.set(src.lev);
  dst.occ.set(src.occ);
  dst.pos.set(src.pos);
  dst.power.set(src.power);
  dst.chg.set(src.chg);
  dst.turn = src.turn;
  dst.phase = src.phase;
  dst.ply = src.ply;
  dst.winner = src.winner;
  dst.reason = src.reason;
  dst.log.length = 0;
}

/** Cheap ordering key — a win first, then height gained, then a power spend last. */
function orderKey(s: State, ply: number): number {
  if (plyBuild(ply) === NONE) return 1e6;
  const to = ply & 63;
  return s.lev[to] * 100 - ((ply >>> 14) & 7);
}

function negamax(ctx: Ctx, s: State, depth: number, alpha: number, beta: number, dist: number): number {
  if ((ctx.nodes & 255) === 0) {
    if (ctx.deadline > 0 && Date.now() > ctx.deadline) ctx.aborted = true;
    if (ctx.maxNodes > 0 && ctx.nodes > ctx.maxNodes) ctx.aborted = true;
  }
  if (ctx.aborted) return 0;
  ctx.nodes++;

  const plies = genPlies(s);
  if (plies.length === 0) return -MATE + dist; // sealed in: the side to move has lost
  if (depth <= 0) return evaluate(s, s.turn, ctx.flat);

  plies.sort((a, b) => orderKey(s, b) - orderKey(s, a));

  let best = -Infinity;
  const child = ctx.pool[dist];
  for (const ply of plies) {
    copyInto(child, s);
    applyPly(child, ply, undefined, false);
    let score: number;
    if (isOver(child)) {
      score = MATE - dist; // an immediate climb — nothing after it matters
    } else {
      score = -negamax(ctx, child, depth - 1, -beta, -alpha, dist + 1);
    }
    if (ctx.aborted) return 0;
    if (score > best) best = score;
    if (best > alpha) alpha = best;
    if (alpha >= beta) break;
  }
  return best;
}

/** Iterative deepening from the root. Returns the best ply found within the budget. */
export function searchTurn(s: State, opts: SearchOpts): SearchResult {
  const started = Date.now();
  const maxDepth = Math.max(1, opts.depth);
  const ctx: Ctx = {
    pool: Array.from({ length: maxDepth + 3 }, () => cloneState(s)),
    nodes: 0,
    deadline: opts.timeMs && opts.timeMs > 0 ? started + opts.timeMs : 0,
    maxNodes: opts.nodes ?? 0,
    aborted: false,
    flat: !!opts.flat,
  };

  const root = genPlies(s);
  if (root.length === 0) return { ply: -1, score: -MATE, depth: 0, nodes: 0, ms: 0 };
  root.sort((a, b) => orderKey(s, b) - orderKey(s, a));

  const noise = opts.noise ?? 0;
  const rng = opts.rng ?? makeRng(s.seed ^ (s.ply * 2654435761));

  let bestPly = root[0];
  let bestScore = -Infinity;
  let reached = 0;

  for (let depth = 1; depth <= maxDepth; depth++) {
    let localBest = -Infinity;
    let localPly = bestPly;
    let alpha = -Infinity;
    const child = ctx.pool[0];

    // Search the previous iteration's best first — that is what makes deepening pay.
    const ordered = [bestPly, ...root.filter((p) => p !== bestPly)];
    for (const ply of ordered) {
      copyInto(child, s);
      applyPly(child, ply, undefined, false);
      let score: number;
      if (isOver(child)) {
        score = MATE;
      } else {
        score = -negamax(ctx, child, depth - 1, -Infinity, -alpha, 1);
      }
      if (ctx.aborted) break;
      if (noise > 0) score += (rng() - 0.5) * 2 * noise;
      if (score > localBest) {
        localBest = score;
        localPly = ply;
      }
      if (score > alpha) alpha = score;
    }
    if (ctx.aborted) break;
    bestPly = localPly;
    bestScore = localBest;
    reached = depth;
    if (bestScore >= MATE - depth) break; // a forced win found — deeper cannot improve on it
  }

  return { ply: bestPly, score: bestScore, depth: reached, nodes: ctx.nodes, ms: Date.now() - started };
}

// ── the opening ─────────────────────────────────────────────────────────────────

/**
 * Placement is not searched exhaustively (25×24×23×22 orderings); each empty cell is scored by the
 * position it produces, which is exactly what the eval already measures.
 */
export function choosePlacement(s: State, opts: SearchOpts): number {
  const seat = s.turn;
  const legal = genPlies(s);
  if (legal.length === 0) return -1;
  const rng = opts.rng ?? makeRng((s.seed ^ (s.ply * 40503)) >>> 0);
  const probe = cloneState(s);
  let best = legal[0];
  let bestScore = -Infinity;
  for (const ply of legal) {
    copyInto(probe, s);
    applyPly(probe, ply, undefined, false);
    const cell = ply & 63;
    let score = evaluate(probe, seat, !!opts.flat);
    // Standing high is worth taking, and so is having somewhere to climb from turn one.
    score += s.lev[cell] * 18;
    score += (rng() - 0.5) * 12;
    if (score > bestScore) {
      bestScore = score;
      best = packPlace(cell);
    }
  }
  return best;
}

/**
 * Draft. The offered row is public, so the pick is a simple preference order softened by the tier's
 * noise — a bot that always takes the same tile makes Charter a script.
 */
// Indexed by power id, from the measured neutral-draft win rates rather than from taste — the first
// version of this table preferred the weakest tile in the pool and handed seat 0 a losing draft.
const DRAFT_PREF = [0, 5, 1, 2, 4, 3]; // none, mason, warden, gale, courier, scout
export function chooseDraft(s: State, opts: SearchOpts): number {
  const legal = genPlies(s);
  if (legal.length === 0) return -1;
  const rng = opts.rng ?? makeRng((s.seed ^ 0x5f3a) >>> 0);
  let best = legal[0];
  let bestScore = -Infinity;
  for (const ply of legal) {
    const idx = ply & 63;
    const id = s.offer[idx];
    const score = (DRAFT_PREF[id] ?? 0) + (rng() - 0.5) * (opts.flat ? 6 : 2);
    if (score > bestScore) {
      bestScore = score;
      best = ply;
    }
  }
  return best;
}

/** The one entry point the game screen uses: pick a ply for whatever phase we are in. */
export function chooseAny(s: State, opts: SearchOpts): number {
  if (s.phase === PH_PLACE) return choosePlacement(s, opts);
  if (s.phase === PH_PLAY) return searchTurn(s, opts).ply;
  return chooseDraft(s, opts);
}

export function optsForTier(t: Tier, seed = 0): SearchOpts {
  return {
    depth: t.depth,
    timeMs: t.timeMs,
    flat: t.flat,
    noise: t.flat ? 14 : 3,
    rng: makeRng((seed ^ 0x9e37) >>> 0),
  };
}

/**
 * A one-ply greedy control for the balance sim: take a win, otherwise stand as high as you can.
 * Deliberately NOT the Novice tier — a control the sim compares against must be a different animal,
 * or "the bot beats the control" is just the bot beating itself.
 */
export function greedyPly(s: State, rng: Rng): number {
  const plies = genPlies(s);
  if (plies.length === 0) return -1;
  let best = plies[0];
  let bestScore = -Infinity;
  for (const ply of plies) {
    let score: number;
    if (plyBuild(ply) === NONE) score = 1e6;
    else score = s.lev[ply & 63] * 10 + rng();
    if (score > bestScore) {
      bestScore = score;
      best = ply;
    }
  }
  return best;
}

export function randomPly(s: State, rng: Rng): number {
  const plies = genPlies(s);
  if (plies.length === 0) return -1;
  return plies[Math.floor(rng() * plies.length)];
}
